import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';

export const fetchOrders = createAsyncThunk('orders/fetch', async (arg) => {
  const { page = 1, limit = 20, status, paymentStatus, locationId } = arg || {};

  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (status) params.set('status', status);
  if (paymentStatus) params.set('paymentStatus', paymentStatus);
  if (locationId) params.set('locationId', locationId);

  // Use merchant orders endpoint
  const res = await axios.get(`/merchants/orders?${params.toString()}`);
  return res.data;
});

export const createOrder = createAsyncThunk('orders/create', async ({ order }) => {
  const res = await axios.post(`/orders`, order);
  return res.data;
});

export const updateOrderStatus = createAsyncThunk('orders/updateStatus', async ({ orderId, status }) => {
  const res = await axios.patch(`/merchants/orders/${orderId}/status`, { status });
  return res.data;
});

export const assignRider = createAsyncThunk('orders/assignRider', async ({ orderId, riderInfo }) => {
  const res = await axios.patch(`/merchants/orders/${orderId}/assign-rider`, riderInfo);
  return res.data;
});

export const updatePaymentStatus = createAsyncThunk('orders/updatePaymentStatus', async ({ orderId, paymentStatus }) => {
  const res = await axios.patch(`/merchants/orders/${orderId}/payment-status`, { paymentStatus });
  return res.data;
});

const ordersSlice = createSlice({
  name: 'orders',
  initialState: {
    items: [],
    appointments: [],
    total: 0,
    appointmentsTotal: 0,
    totalItems: 0,
    totalPages: 0,
    page: 1,
    limit: 20,
    // Stats for summary cards
    stats: {
      totalOrders: 0,
      pendingOrders: 0,
      completedOrders: 0,
      totalRevenue: 0
    },
    status: 'idle',
    error: null
  },
  reducers: {
    orderUpdated(s, a) {
      const index = s.items.findIndex(item => item.id === a.payload.id);
      if (index !== -1) s.items[index] = { ...s.items[index], ...a.payload };
    }
  },
  extraReducers: (b) => {
    b.addCase(fetchOrders.pending, (s) => { s.status = 'loading'; })
     .addCase(fetchOrders.fulfilled, (s, a) => {
       s.status = 'succeeded';
       s.items = a.payload.orders || a.payload;
       s.appointments = a.payload.appointments || [];
       s.total = a.payload.total || a.payload.count || a.payload.orders?.length || 0;
       s.appointmentsTotal = a.payload.appointmentsTotal || 0;
       s.totalItems = a.payload.totalItems || (s.total + s.appointmentsTotal);
       s.totalPages = a.payload.totalPages || 1;
       s.page = a.payload.page || 1;
       s.limit = a.payload.limit || 20;
       // Store stats from backend
       if (a.payload.stats) {
         s.stats = a.payload.stats;
       }
     })
     .addCase(fetchOrders.rejected, (s, a) => { s.status = 'failed'; s.error = a.error.message; })
     .addCase(createOrder.fulfilled, (s, a) => { s.items = [a.payload, ...s.items]; })
     .addCase(updateOrderStatus.fulfilled, (s, a) => {
       const index = s.items.findIndex(item => item.id === a.payload.order?.id || item.id === a.payload.id);
       if (index !== -1) {
         s.items[index] = a.payload.order || a.payload;
       }
     })
     .addCase(assignRider.fulfilled, (s, a) => {
       const index = s.items.findIndex(item => item.id === a.payload.order?.id || item.id === a.payload.id);
       if (index !== -1) {
         s.items[index] = a.payload.order || a.payload;
       }
     })
     .addCase(updatePaymentStatus.fulfilled, (s, a) => {
       const index = s.items.findIndex(item => item.id === a.payload.order?.id || item.id === a.payload.id);
       if (index !== -1) {
         s.items[index] = a.payload.order || a.payload;
       }
     });
  }
});

export const { orderUpdated } = ordersSlice.actions;
export default ordersSlice.reducer;
